import {TextArea} from '@/components/commons/textarea/TextArea'
import {NotesDetails} from '@/components/ui/notes-details/NotesDetails'
import {TreatmentRequestEditButtons} from '@/features/treatment-requests/components/treatment-request-edit-buttons/TreatmentRequestEditButtons'
import {StyledTreatmentRequestDetailsNotesSection} from '@/features/treatment-requests/components/treatment-request-notes-section/style'
import {useTreatmentRequestEditModeView} from '@/features/treatment-requests/hooks/useTreatmentRequestEditModeView'
import {useTreatmentRequestFormSubmission} from '@/features/treatment-requests/hooks/useTreatmentRequestFormSubmission'
import {useUpdateTreatmentRequest} from '@/features/treatment-requests/services/useUpdateTreatmentRequest'
import {useTreatmentRequestDetailsStore} from '@/features/treatment-requests/store/treatmentRequestDetailsStore'
import {TreatmentRequestExtended, TreatmentRequestNotesValidationSchema} from '@/features/treatment-requests/types'
import {remapTreatmentRequestToDefaultValues, remapTreatmentRequestToPayload} from '@/features/treatment-requests/utils'
import {useTranslation} from '@/translations/i18n'
import {getDirtyFieldsFromFormValues, ObjectKeys} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {Controller, FormProvider, useForm, useFormContext} from 'react-hook-form'
import {useOutletContext} from 'react-router-dom'
import {usePermissions} from '@/features/auth/store/store'

export const TreatmentRequestNotesSection = () => {
    const {treatmentRequest} = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()

    const defaultValues = remapTreatmentRequestToDefaultValues(treatmentRequest)

    const form = useForm<TreatmentRequestNotesValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(TreatmentRequestNotesValidationSchema),
        defaultValues: {
            notes: defaultValues.notes
        }
    })

    return (
        <FormProvider {...form}>
            <TreatmentRequestNotesSectionContent treatmentRequest={treatmentRequest} />
        </FormProvider>
    )
}

const TreatmentRequestNotesSectionContent = ({treatmentRequest}: {treatmentRequest: TreatmentRequestExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useTreatmentRequestEditModeView('notes')
    const setIsErrorBannerVisible = useTreatmentRequestDetailsStore(state => state.setIsErrorBannerVisible)
    const form = useFormContext<TreatmentRequestNotesValidationSchema>()
    const permissions = usePermissions()
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useTreatmentRequestFormSubmission({
        formSchema: TreatmentRequestNotesValidationSchema
    })

    // mutation
    const updateTreatmentRequest = useUpdateTreatmentRequest({
        urlParams: {treatmentRequestId: treatmentRequest.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapTreatmentRequestToPayload(dirtyFormValues)
            return updateTreatmentRequest.mutate(parsedValues)
        },
        errors => {
            setIsErrorBannerVisible(true)
            console.error(errors)
            scrollToTop()
        }
    )

    return (
        <StyledTreatmentRequestDetailsNotesSection
            title={t('commons:notes')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.requests?.write && (
                    <TreatmentRequestEditButtons
                        groupName="notes"
                        isConfirmDisabled={!ObjectKeys(form.formState.dirtyFields).length}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                        isDisabled={treatmentRequest.status == 'accepted' || treatmentRequest.status == 'rejected'}
                    />
                )
            }
        >
            {isEditModeEnabled ? (
                <Controller
                    control={form.control}
                    name="notes"
                    render={({field, fieldState}) => <TextArea {...field} errorMessage={fieldState.error?.message} />}
                />
            ) : (
                <NotesDetails notes={treatmentRequest.notes} />
            )}
        </StyledTreatmentRequestDetailsNotesSection>
    )
}
